/** @format */

import {useState, useEffect} from 'react';
import {Auth, Storage} from 'aws-amplify';
import {
  Container,
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Skeleton,
  Stack,
} from '@mui/material';
import {useLoaderData, useParams} from 'react-router-dom';
import type {LoaderFunctionArgs} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';

//MTH component import
import GearMenu, {GearMenuListArgType} from '../../component/GearMenuList';
import {
  LogoUploadDialog,
  LanguageUploadDialog,
} from './WebsiteSettingUploadDialog';
import {
  LogoDeleteDialog,
  FooterDeleteDialog,
} from './WebsiteSettingDeleteDialog';
import {
  UnitEditDialog,
  CountryEditDialog,
  FooterEditDialog,
} from './WebsiteSettingEditDialog';
import CardContents from '../../component/CardContents';
import {LanguageDownloadDialog} from './WebsiteSettingDownloadDialog';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {useUserInfo} from '../my-site/MainMySite';
import {file, iconBlack} from '../../define';
import {addTranslation} from '../../functions/addTranslation';
import getLogo from '../../functions/getLogo';
import {
  getCompositionsGateway,
  getCompositionsSites,
} from '../../API/API_Collection';

type LoaderReturn = {
  maccessLogo: string;
  mhiLogo: string;
  languageUpdateTime: Date | null;
};
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<LoaderReturn | undefined> {
  try {
    //言語ファイルを取得
    Storage.configure({customPrefix: {private: file.LANGUAGE_S3_KEY_PREFIX}});
    /** 言語ファイル一覧 */
    const languageList = await Storage.list('', {
      level: 'private',
      pageSize: 1000, //ワーニング除去の為。運用的に対象のバケットに1000個より多いオブジェクトは保存しない想定
    });
    /** 言語ファイルの有無 */
    const customTranslationInfo = languageList.results.find(fileData => {
      return fileData.key === file.CUSTOM_TRANSLATION_NAME;
    });
    /** 言語ファイルの最終更新時刻 */
    let languageLastModified: LoaderReturn['languageUpdateTime'] = null;

    //言語ファイルがある場合、S3内の言語ファイルを取得
    if (customTranslationInfo !== undefined) {
      if (customTranslationInfo.lastModified !== undefined) {
        languageLastModified = customTranslationInfo.lastModified;
      }

      const languageFile = await Storage.get(file.CUSTOM_TRANSLATION_NAME, {
        level: 'private',
      });
      //ファイルを読み込む
      const fetchResponse = await fetch(languageFile);
      const langDataTxt = await fetchResponse.text();
      //言語データを翻訳リソースに追加する
      addTranslation(langDataTxt);
    }

    //ロゴ画像を取得
    /** M-ACCESSのロゴ画像 */
    let maccessLogo = '';
    /** MHIのロゴ画像 */
    let mhiLogo = '';
    await getLogo(
      logo => {
        maccessLogo = logo;
      },
      logo => {
        mhiLogo = logo;
      }
    );

    return {
      maccessLogo: maccessLogo,
      mhiLogo: mhiLogo,
      languageUpdateTime: languageLastModified,
    };
  } catch {
    return undefined;
  }
}

/**
 * 表示設定
 * @returns JSX
 */
const DisplaySetting = () => {
  const trans = useTranslation().t; //翻訳関数
  const loaderData = useLoaderData() as LoaderReturn | undefined;
  //ユーザ情報取得
  const {userInfo} = useUserInfo();
  //サイトID取得
  const {siteID} = useParams();

  //編集対象
  type selectType =
    | 'country'
    | 'maccess'
    | 'mhi'
    | 'unit'
    | 'language'
    | 'footer'
    | '';
  const [selected, setSelected] = useState<selectType>('');
  //M-ACCESSのロゴデータ（取得できない場合はデフォルト画像のパスが入る）
  const [maccessLogo, setMaccessLogo] = useState<string>('');
  //MHIのロゴデータ（取得できない場合はデフォルト画像のパスが入る）
  const [mhiLogo, setMHILogo] = useState<string>('');
  //prettier-ignore
  //フッターテキストデータ
  const [footerText, setFooterText] = useState<apiIF.res.getSettingUser['footer-text']>('');
  //prettier-ignore
  //単位
  const [unit, setUnit] = useState<apiIF.res.getSettingUser['unit']>({pressure: 'MPa'});
  //prettier-ignore
  //ユーザ所属国
  const [country, setCountry] = useState<apiIF.res.getSettingUser['country']>('');
  //prettier-ignore
  //言語ファイル最終更新時刻
  const [languageUpdateTime, setLanguageUpdateTime] = useState<Date | null>(null);
  //支払いユーザ判定
  const [payingUser, setPayingUser] = useState<boolean>(false);

  //ダイアログ表示フラグ
  const [isOpenDownloadDialog, setIsOpenDownloadDialog] = useState(false); //ダウンロードダイアログ
  const [isOpenUploadDialog, setIsOpenUploadDialog] = useState(false); //アップロードダイアログ
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false); //削除ダイアログ
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false); //編集ダイアログ

  useEffect(() => {
    getCompositionsSites({
      'user-authority': 'admin',
      'fields': 'billing-status',
      'max-result': 100,
    }).then(response => {
      // 全サイトのサイトIDを取得
      const paymentSiteInfo = response['site-informations'].filter(
        siteInfo => siteInfo['billing-status'] !== 'unpaid'
      );
      // サイトが1つも無い場合は終了
      if (paymentSiteInfo.length === 0) return;
      //ログインユーザのユーザ情報を取得
      Auth.currentAuthenticatedUser().then(response => {
        /** ユーザID */
        const userID = response['username'];
        paymentSiteInfo.forEach(siteInfo => {
          //サイトに登録されているゲートウェイ情報を取得
          getCompositionsGateway(siteInfo['site-id'], {
            'max-result': 100,
          }).then(response => {
            //ユーザが支払いユーザであるか調べる
            if (
              response['gateway-informations'].some(gwInfo => {
                return gwInfo['billing-user-id'] === userID;
              })
            ) {
              setPayingUser(true);
            }
          });
        });
      });
    });
  }, [siteID]);

  useEffect(() => {
    if (loaderData !== undefined) {
      setMaccessLogo(loaderData.maccessLogo);
      setMHILogo(loaderData.mhiLogo);
      setLanguageUpdateTime(loaderData.languageUpdateTime);
    }
    if (userInfo !== null) {
      setFooterText(userInfo['footer-text']);
      setUnit(userInfo.unit);
      setCountry(userInfo.country);
    }
  }, [loaderData, userInfo]);
  /**
   * ダウンロードボタン押下時処理
   */
  const openDownloadDialog = (type: selectType) => {
    setIsOpenDownloadDialog(true);
    setSelected(type);
  };
  /**
   * ロゴ画像、フッターアップロードボタン押下時処理
   */
  const openUploadDialog = (type: selectType) => {
    setIsOpenUploadDialog(true);
    setSelected(type);
  };
  /**
   * ロゴ画像、フッター削除ボタン押下時処理
   */
  const openDeleteDialog = (type: selectType) => {
    setIsOpenDeleteDialog(true);
    setSelected(type);
  };
  /**
   * 単位編集ボタン押下時処理
   */
  const openEditDialog = (type: selectType) => {
    setIsOpenEditDialog(true);
    setSelected(type);
  };

  /**
   * 歯車メニューリスト
   * @returns メニュー一覧
   */
  const gearMenuItem = (type: selectType): GearMenuListArgType['menuItem'] => {
    /** アップロードボタン用ラベル */
    let uploadLabel = '';
    /** ダウンロードボタン用ラベル */
    let downloadLabel = '';
    /** 初期化ボタン用ラベル */
    let defaultLabel = '';
    /** 編集ボタン用ラベル */
    let editLabel = '';

    /** 描画する項目 */
    const renderItem = [];
    switch (type) {
      case 'maccess': //fall though
      case 'mhi':
        uploadLabel = trans('DisplaySetting.menuItem.logo.upload');
        defaultLabel = trans('DisplaySetting.menuItem.logo.default');
        break;
      case 'unit':
        editLabel = trans('DisplaySetting.menuItem.unit.edit');
        break;
      case 'language':
        downloadLabel = trans('DisplaySetting.menuItem.language.download');
        uploadLabel = trans('DisplaySetting.menuItem.language.upload');
        break;
      case 'footer':
        editLabel = trans('DisplaySetting.menuItem.footer.edit');
        defaultLabel = trans('DisplaySetting.menuItem.footer.default');
        break;
      case 'country':
        editLabel = trans('DisplaySetting.menuItem.country.edit');
        break;
    }
    if (uploadLabel !== '') {
      renderItem.push({
        label: uploadLabel,
        onClick: () => openUploadDialog(type),
        icon: {
          className: 'accountSetting__cardContents__button__icon',
          src: iconBlack.UPLOAD.src,
          alt: trans(
            `DisplaySetting.image.alternativeText.${iconBlack.UPLOAD.alt}`
          ),
        },
      });
    }
    if (downloadLabel !== '') {
      renderItem.push({
        label: downloadLabel,
        onClick: () => openDownloadDialog(type),
        icon: {
          className: 'accountSetting__cardContents__button__icon',
          src: iconBlack.DOWNLOAD.src,
          alt: trans(
            `DisplaySetting.image.alternativeText.${iconBlack.DOWNLOAD.alt}`
          ),
        },
      });
    }
    if (editLabel !== '') {
      renderItem.push({
        label: editLabel,
        onClick: () => openEditDialog(type),
        icon: {
          className: 'accountSetting__cardContents__button__icon',
          src: iconBlack.EDIT.src,
          alt: trans(
            `DisplaySetting.image.alternativeText.${iconBlack.EDIT.alt}`
          ),
        },
      });
    }
    if (defaultLabel !== '') {
      renderItem.push({
        label: defaultLabel,
        onClick: () => openDeleteDialog(type),
        MuiIcon: () => <SettingsBackupRestoreIcon color="secondary" />,
      });
    }
    return renderItem;
  };

  /**
   * 月の名前(略称)を含む配列
   */
  const monthName = [
    trans('DisplaySetting.body.language.description.january'),
    trans('DisplaySetting.body.language.description.february'),
    trans('DisplaySetting.body.language.description.march'),
    trans('DisplaySetting.body.language.description.april'),
    trans('DisplaySetting.body.language.description.may'),
    trans('DisplaySetting.body.language.description.june'),
    trans('DisplaySetting.body.language.description.july'),
    trans('DisplaySetting.body.language.description.august'),
    trans('DisplaySetting.body.language.description.september'),
    trans('DisplaySetting.body.language.description.october'),
    trans('DisplaySetting.body.language.description.november'),
    trans('DisplaySetting.body.language.description.december'),
  ];

  return (
    <Container fixed className="accountSetting">
      <Stack spacing={3}>
        {/** タイトル */}
        <Box>
          <h1>{trans('DisplaySetting.title')}</h1>
        </Box>
        {/** 設定項目一覧 */}
        <Box>
          <Stack spacing={2}>
            {/** 課金用設定 */}
            <Box>
              <Grid container rowSpacing={1} justifyContent="center">
                <Grid item xs={12}>
                  <h3>{trans('DisplaySetting.subTitle.countrySetting')}</h3>
                </Grid>
                {/** 所在国設定 */}
                <Grid item xs={10}>
                  <Card className="accountSetting__cardContents">
                    {/* 歯車ボタン */}
                    <CardHeader
                      title={
                        <Box className="accountSetting__cardContents__header__title">
                          {trans('DisplaySetting.heading.country')}
                        </Box>
                      }
                      action={<GearMenu menuItem={gearMenuItem('country')} />}
                      className="accountSetting__cardContents__header"
                    />
                    {selected === 'country' && (
                      <CountryEditDialog
                        isOpenDialog={isOpenEditDialog}
                        setIsOpenDialog={setIsOpenEditDialog}
                        country={country}
                        payingUser={payingUser}
                      />
                    )}
                    {/* 所在国 */}
                    <CardContent>
                      <CardContents
                        valueDescription={trans(
                          'DisplaySetting.body.country.description'
                        )}
                        value={
                          country === ''
                            ? `${trans(
                                'DisplaySetting.body.country.value.none'
                              )}`
                            : `${trans(
                                `DisplaySetting.body.country.value.${country}`
                              )}`
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
            {/** 表示設定 */}
            <Box>
              <Grid container rowSpacing={1} justifyContent="center">
                <Grid item xs={12}>
                  <h3>{trans('DisplaySetting.subTitle.unitLanguageFooter')}</h3>
                </Grid>
                {/** 圧力単位切り替え */}
                <Grid item xs={10}>
                  <Card className="accountSetting__cardContents">
                    {/* 歯車ボタン */}
                    <CardHeader
                      title={
                        <Box className="accountSetting__cardContents__header__title">
                          {trans('DisplaySetting.heading.unit')}
                        </Box>
                      }
                      action={<GearMenu menuItem={gearMenuItem('unit')} />}
                      className="accountSetting__cardContents__header"
                    />
                    {selected === 'unit' && (
                      <UnitEditDialog
                        isOpenDialog={isOpenEditDialog}
                        setIsOpenDialog={setIsOpenEditDialog}
                        unit={unit}
                        setUnit={setUnit}
                      />
                    )}
                    {/* 圧力単位 */}
                    <CardContent>
                      <CardContents
                        valueDescription={trans(
                          'DisplaySetting.body.unit.pressure.description'
                        )}
                        value={unit.pressure}
                      />
                    </CardContent>
                  </Card>
                </Grid>
                {/** 言語 */}
                <Grid item xs={10}>
                  <Card className="accountSetting__cardContents">
                    {/** タイトル */}
                    <CardHeader
                      title={
                        <Box className="accountSetting__cardContents__header__title">
                          {trans('DisplaySetting.heading.language')}
                        </Box>
                      }
                      action={<GearMenu menuItem={gearMenuItem('language')} />}
                      className="accountSetting__cardContents__header"
                    />
                    {selected === 'language' && (
                      <>
                        <LanguageUploadDialog
                          fileName={file.CUSTOM_TRANSLATION_NAME}
                          isOpenDialog={isOpenUploadDialog}
                          setIsOpenDialog={setIsOpenUploadDialog}
                        />
                        <LanguageDownloadDialog
                          isOpenDialog={isOpenDownloadDialog}
                          setIsOpenDialog={setIsOpenDownloadDialog}
                        />
                      </>
                    )}
                    <CardContent>
                      {languageUpdateTime === null
                        ? trans(
                            'DisplaySetting.body.language.description.unregistered'
                          )
                        : trans(
                            'DisplaySetting.body.language.description.registered',
                            {
                              day: languageUpdateTime.getDate(),
                              month: monthName[languageUpdateTime.getMonth()],
                              year: languageUpdateTime.getFullYear(),
                              hour: languageUpdateTime.getHours(),
                              minute: languageUpdateTime.getMinutes(),
                              second: languageUpdateTime.getSeconds(),
                            }
                          )}
                    </CardContent>
                  </Card>
                </Grid>
                {/** フッター */}
                <Grid item xs={10}>
                  <Card className="accountSetting__cardContents">
                    {/* 歯車ボタン */}
                    <CardHeader
                      title={
                        <Box className="accountSetting__cardContents__header__title">
                          {trans('DisplaySetting.heading.footer')}
                        </Box>
                      }
                      action={<GearMenu menuItem={gearMenuItem('footer')} />}
                      className="accountSetting__cardContents__header"
                    />
                    {selected === 'footer' && (
                      <>
                        <FooterEditDialog
                          footer={footerText}
                          isOpenDialog={isOpenEditDialog}
                          setIsOpenDialog={setIsOpenEditDialog}
                        />
                        <FooterDeleteDialog
                          isOpenDialog={isOpenDeleteDialog}
                          setIsOpenDialog={setIsOpenDeleteDialog}
                        />
                      </>
                    )}
                    {/* フッタ */}
                    <CardContent>
                      <Box className="accountSetting__cardContents__body__footer">
                        {footerText}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
            {/** ロゴ画像設定 */}
            <Box>
              <Grid
                container
                rowSpacing={1}
                columnSpacing={2}
                justifyContent="center">
                <Grid item xs={12}>
                  <h3>{trans('DisplaySetting.subTitle.logo')}</h3>
                </Grid>
                {/* M-ACCESSロゴ */}
                <Grid item xs={4.75}>
                  <Card className="accountSetting__cardContents">
                    {/* 歯車ボタン */}
                    <CardHeader
                      title={
                        <Box className="accountSetting__cardContents__header__title">
                          {trans('DisplaySetting.heading.logo.mAccess')}
                        </Box>
                      }
                      action={<GearMenu menuItem={gearMenuItem('maccess')} />}
                      className="accountSetting__cardContents__header"
                    />
                    {selected === 'maccess' && (
                      <>
                        <LogoUploadDialog
                          logoName={file.MACCESS_LOGO_NAME}
                          isOpenDialog={isOpenUploadDialog}
                          setIsOpenDialog={setIsOpenUploadDialog}
                        />
                        <LogoDeleteDialog
                          logoName={file.MACCESS_LOGO_NAME}
                          isOpenDialog={isOpenDeleteDialog}
                          setIsOpenDialog={setIsOpenDeleteDialog}
                        />
                      </>
                    )}
                    {/* ロゴ */}
                    <CardContent>
                      <Box className="accountSetting__cardContents__body__logo">
                        {maccessLogo === '' ? (
                          <Skeleton variant="rectangular">
                            <Box className="accountSetting__cardContents__body__logo__box--maccess"></Box>
                          </Skeleton>
                        ) : (
                          <Box className="accountSetting__cardContents__body__logo__box--maccess">
                            <img
                              src={maccessLogo}
                              alt={`${trans(
                                'DisplaySetting.body.logo.image.alternativeText.mAccessLogo'
                              )}`}
                              className="accountSetting__cardContents__body__logo__box__image"
                            />
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                {/* MITSUBISHIロゴ */}
                <Grid item xs={4.75}>
                  <Card className="accountSetting__cardContents">
                    {/* 歯車ボタン */}
                    <CardHeader
                      title={
                        <Box className="accountSetting__cardContents__header__title">
                          {trans('DisplaySetting.heading.logo.mitsubishi')}
                        </Box>
                      }
                      action={<GearMenu menuItem={gearMenuItem('mhi')} />}
                      className="accountSetting__cardContents__header"
                    />
                    {selected === 'mhi' && (
                      <>
                        <LogoUploadDialog
                          logoName={file.MHI_LOGO_NAME}
                          isOpenDialog={isOpenUploadDialog}
                          setIsOpenDialog={setIsOpenUploadDialog}
                        />
                        <LogoDeleteDialog
                          logoName={file.MHI_LOGO_NAME}
                          isOpenDialog={isOpenDeleteDialog}
                          setIsOpenDialog={setIsOpenDeleteDialog}
                        />
                      </>
                    )}
                    {/* ロゴ */}
                    <CardContent>
                      <Box className="accountSetting__cardContents__body__logo">
                        {mhiLogo === '' ? (
                          <Skeleton variant="rectangular">
                            <Box className="accountSetting__cardContents__body__logo__box--mhi"></Box>
                          </Skeleton>
                        ) : (
                          <Box className="accountSetting__cardContents__body__logo__box--mhi">
                            <img
                              src={mhiLogo}
                              alt={`${trans(
                                'DisplaySetting.body.logo.image.alternativeText.mhiLogo'
                              )}`}
                              className="accountSetting__cardContents__body__logo__box__image"
                            />
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
};
export default DisplaySetting;
